import {
  ensureError,
  ensureHelp,
  ensureLabel,
  INPUT_BASE_CLASS,
  type FieldRenderContext,
  type FieldType,
} from './base.js';
import {
  validateLength,
  validatePattern,
  validateRequired,
  type ValidationResult,
} from '../validation.js';

function ensureInput(ctx: FieldRenderContext, inputType: string): HTMLInputElement {
  let input = ctx.host.querySelector<HTMLInputElement>(':scope > input');
  if (!input) {
    input = document.createElement('input');
    input.className = INPUT_BASE_CLASS;
    ctx.host.insertBefore(input, ctx.host.querySelector(':scope > .plp-field-error'));
  }
  input.type = inputType;
  input.name = ctx.name;
  input.required = ctx.required;
  if (ctx.placeholder !== undefined) input.placeholder = ctx.placeholder;
  if (ctx.defaultValue !== undefined && !input.value) input.value = ctx.defaultValue;
  return input;
}

export const textType: FieldType = {
  render(ctx) {
    ensureLabel(ctx.host, ctx.label, ctx.required);
    const input = ensureInput(ctx, 'text');
    const pattern = ctx.host.getAttribute('pattern');
    const minlength = ctx.host.getAttribute('minlength');
    const maxlength = ctx.host.getAttribute('maxlength');
    if (pattern) input.pattern = pattern;
    if (minlength) input.minLength = parseInt(minlength, 10);
    if (maxlength) input.maxLength = parseInt(maxlength, 10);
    ensureError(ctx.host);
    ensureHelp(ctx.host, ctx.help);
  },

  getValue(ctx) {
    return ctx.host.querySelector<HTMLInputElement>(':scope > input')?.value ?? '';
  },

  validate(ctx) {
    const value = String(this.getValue(ctx));
    if (ctx.required) {
      const r = validateRequired(value);
      if (!r.valid) return withCustomMessage(r, ctx);
    }
    if (value === '') return { valid: true };

    const pattern = ctx.host.getAttribute('pattern');
    if (pattern) {
      const r = validatePattern(value, pattern);
      if (!r.valid) return withCustomMessage(r, ctx);
    }
    const min = ctx.host.getAttribute('minlength');
    const max = ctx.host.getAttribute('maxlength');
    const r = validateLength(
      value,
      min ? parseInt(min, 10) : undefined,
      max ? parseInt(max, 10) : undefined
    );
    if (!r.valid) return withCustomMessage(r, ctx);
    return { valid: true };
  },

  shouldSubmit() {
    return true;
  },

  setDisabled(ctx, disabled) {
    const input = ctx.host.querySelector<HTMLInputElement>(':scope > input');
    if (input) input.disabled = disabled;
  },
};

export function withCustomMessage(r: ValidationResult, ctx: FieldRenderContext): ValidationResult {
  if (r.valid) return r;
  return { valid: false, error: ctx.errorMessage ?? r.error };
}

export { ensureInput };
